import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataCatalogModal } from "../dataCatalog/DataCatalog";
import { useNavigate } from "react-router-dom";
import { newtonsCradle } from "ldrs";
import { searchSuggestions } from "../../pages/search/config";
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/solid";
import {
  setQuery,
  setParameters,
  setResults,
  setLoading,
} from "../../store/searchPage";
import { generateQueryString } from "../../util/queryStringGenerator";
import apiClient from "../../util/axios";

newtonsCradle.register();

const CAROUSEL_INTERVAL = 3000; // Change suggestion interval

const SearchBar = ({
  queryString = null,
  useCarousel = true,
  staticPlaceholder = "Search...",
}) => {
  const dispatch = useDispatch();
  const query = useSelector((state) => state.search.query);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (useCarousel) {
      const interval = setInterval(() => {
        setCurrentSuggestionIndex(
          (prevIndex) => (prevIndex + 1) % searchSuggestions.length
        );
      }, CAROUSEL_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [useCarousel]);

  const handleChange = (e) => {
    dispatch(setQuery(queryString || e.target.value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    navigate(`/search-results?query=${encodeURIComponent(query)}`);
  };

  const handleApplySelection = async (selections) => {
    try {
      // Generate query string first
      const queryString = generateQueryString(selections);

      // Store the parameters and results in Redux before navigation
      dispatch(setParameters(selections));
      dispatch(setQuery(queryString));

      // Single navigation at the end
      navigate(
        `/search-results?query=${encodeURIComponent(queryString)}&fromDataCatalog=true`,
        {
          replace: true,
        }
      );

      // Execute search after navigation
      dispatch(setLoading(true));
      const response = await apiClient.post("/api/search/execute", selections);
      dispatch(setResults(response.data));
      dispatch(setLoading(false));
    } catch (error) {
      console.error("Error executing search:", error);
      dispatch(setLoading(false));
    }
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-xl h-full flex items-center"
      >
        <div className="relative w-full h-full flex items-center">
          <label for="search-bar" className="hidden">
            Search
          </label>
          <input
            id="search-bar"
            type="text"
            value={query}
            onChange={handleChange}
            className="w-full h-16 font-inter font-lighter text-sm px-10 pr-24 py-auto border border-gray-100 rounded-full shadow-xl focus:outline-none focus:ring-2 focus:ring-morange-200"
            placeholder={
              useCarousel
                ? searchSuggestions[currentSuggestionIndex] || "Search..."
                : staticPlaceholder
            }
          />
          <div className="absolute right-16 h-6 border-l border-gray-300"></div>{" "}
          {/* Vertical line */}
          <AdjustmentsHorizontalIcon
            className="h-5 w-5 fill-slate-400 absolute right-10 cursor-pointer"
            onClick={() => setIsCatalogOpen(true)}
          />
          <button
            type="submit"
            className="absolute right-2"
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="h-5 w-5 fill-slate-400 mr-1" />
          </button>
        </div>
      </form>
      <DataCatalogModal
        isOpen={isCatalogOpen}
        onClose={() => setIsCatalogOpen(false)}
        onApplySelection={handleApplySelection}
      />
    </div>
  );
};

export default SearchBar;
